/**
 * Serialisation guard for room switches.
 *
 * setCurrentRoomId() is re-entrant and awaits mid-flight (leaveMeeting, then
 * joinMeetingRoom), so with rapid consecutive switches two calls interleave and whichever
 * one resumes LAST wins every write after its await. That is how a superseded switch used to
 * strand the user: switching A → B → C, if C's leave resolved first, C joined and then B's
 * stale continuation overwrote currentRoomId back to B and issued its own join, which
 * joinJitsiMeeting accepted as the newest request and chased after C. The client ended up in
 * B while the UI said C — so the participant grid held skeletons for people who were never
 * going to arrive, then emptied when the skeleton window expired.
 *
 * Each switch takes a guard and re-checks isSuperseded() after every await; once a newer
 * switch has begun, the older one stops touching shared state and lets the newer one own the
 * conference.
 */

let switchSequence = 0;
let inFlightTargetRoomPid: string | null = null;

export interface RoomSwitchGuard {
  /** True once a newer switch has started: stop writing shared state and return. */
  isSuperseded: () => boolean;
  /** Release the in-flight marker. Safe to call from a finally, superseded or not. */
  finish: () => void;
}

export const beginRoomSwitch = (targetRoomPid: string): RoomSwitchGuard => {
  const sequence = ++switchSequence;
  inFlightTargetRoomPid = targetRoomPid;

  return {
    isSuperseded: () => switchSequence !== sequence,
    finish: () => {
      // Only the newest switch may clear the marker — a superseded one finishing later must
      // not advertise the newer switch's target as done.
      if (switchSequence === sequence) inFlightTargetRoomPid = null;
    },
  };
};

/**
 * Whether a switch into this exact room is already running.
 *
 * setCurrentRoomId's own "already in this room" check reads currentRoomId, which isn't
 * updated until after the leave completes, so it cannot see a switch that is still in
 * flight. Without this, a double-click on a room (or an effect firing twice) started a second
 * full leave+join for the room already being entered.
 */
export const isRoomSwitchInFlightFor = (roomPid: string): boolean =>
  !!roomPid && inFlightTargetRoomPid === roomPid;
